import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUser, FiArrowRight, FiBookOpen, FiTrendingUp, FiSearch } = FiIcons;

function Blog() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const blogPosts = [
    {
      title: "Why Every Event Deserves Its Own Digital Memory Wall",
      excerpt: "Discover how a digital memory wall transforms every event into a timeless experience — connecting people, photos, and stories in one living album.",
      author: "Sarah Chen",
      date: "March 18, 2024",
      readTime: "4 min read",
      category: "Digital Innovation",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop",
      featured: true,
      slug: "digital-memory-wall-events"
    },
    {
      title: "10 Creative Ways to Encourage Photo Sharing at Your Event",
      excerpt: "Transform your event into a memory-making machine with these proven strategies that get every guest excited about capturing and sharing moments.",
      author: "Sarah Chen",
      date: "March 15, 2024",
      readTime: "5 min read",
      category: "Event Tips",
      image: "https://images.unsplash.com/photo-1511578314322-379afb476865?w=600&h=400&fit=crop",
      featured: false,
      slug: "creative-ways-encourage-photo-sharing"
    },
    {
      title: "The Psychology of Memory: Why We Share Photos",
      excerpt: "Discover the fascinating science behind why humans are driven to capture and share experiences, and how it strengthens our connections.",
      author: "Dr. Emily Rodriguez",
      date: "March 12, 2024",
      readTime: "8 min read",
      category: "Psychology",
      image: "https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=600&h=400&fit=crop",
      featured: false,
      slug: "psychology-of-memory"
    },
    {
      title: "Wedding Photography in the Digital Age",
      excerpt: "How modern couples are revolutionizing wedding photography by involving their guests in creating comprehensive visual stories.",
      author: "Marcus Johnson",
      date: "March 8, 2024",
      readTime: "6 min read",
      category: "Weddings",
      image: "https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop",
      featured: false,
      slug: "wedding-photography-digital-age"
    },
    {
      title: "Corporate Event Memories That Matter",
      excerpt: "Learn how leading companies are using shared photo galleries to boost employee engagement and create lasting corporate culture.",
      author: "Jennifer Liu",
      date: "March 5, 2024",
      readTime: "7 min read",
      category: "Corporate",
      image: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=600&h=400&fit=crop",
      featured: false,
      slug: "corporate-event-memories"
    },
    {
      title: "Privacy in the Age of Instant Sharing",
      excerpt: "Balancing the joy of sharing memories with the need for privacy and security in today's digital landscape.",
      author: "Alex Thompson",
      date: "March 1, 2024",
      readTime: "9 min read",
      category: "Privacy & Security",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop",
      featured: false,
      slug: "privacy-instant-sharing"
    },
    {
      title: "The Future of Event Documentation",
      excerpt: "Exploring emerging technologies like AI curation, AR filters, and collaborative storytelling that will shape how we capture events.",
      author: "David Park",
      date: "February 28, 2024",
      readTime: "10 min read",
      category: "Technology",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop",
      featured: false,
      slug: "future-event-documentation"
    }
  ];

  const categories = ["All", "Digital Innovation", "Event Tips", "Psychology", "Weddings", "Corporate", "Privacy & Security", "Technology"];

  // Filter posts based on category and search term
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.author.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get featured post (only if no filters are applied)
  const featuredPost = searchTerm === '' && selectedCategory === 'All' ? filteredPosts[0] : null;
  // Regular posts (exclude featured post if it exists)
  const regularPosts = featuredPost ? filteredPosts.slice(1) : filteredPosts;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-rose-50 pt-16">
      {/* Hero Section */}
      <motion.section 
        className="py-20 px-4"
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
            variants={fadeInUp}
          >
            Our <span className="bg-gradient-to-r from-teal-500 via-cyan-500 to-rose-500 bg-clip-text text-transparent">Blog</span>
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed"
            variants={fadeInUp}
          >
            Stories, insights, and tips about creating unforgettable memories
          </motion.p>
        </div>
      </motion.section>

      {/* Search Bar */}
      {(searchTerm !== '' || selectedCategory !== 'All') && (
        <motion.section 
          className="pb-8 px-4"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <div className="max-w-2xl mx-auto">
            <motion.div 
              className="bg-white rounded-full shadow-lg p-2 flex items-center"
              variants={fadeInUp}
            >
              <SafeIcon icon={FiSearch} className="text-gray-400 ml-4 mr-2" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 outline-none text-gray-900"
              />
              {(searchTerm || selectedCategory !== 'All') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('All');
                  }}
                  className="text-gray-400 hover:text-gray-600 mr-4 text-sm"
                >
                  Clear
                </button>
              )}
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Featured Post */}
      {featuredPost && (
        <motion.section 
          className="pb-12 px-4"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="bg-white rounded-3xl shadow-2xl overflow-hidden"
              variants={fadeInUp}
            >
              <div className="md:flex">
                <div className="md:w-1/2">
                  <img 
                    src={featuredPost.image} 
                    alt={featuredPost.title}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-8 md:p-12">
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Featured
                    </span>
                    <span className="text-teal-600 text-sm font-semibold">{featuredPost.category}</span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{featuredPost.title}</h2>
                  <p className="text-gray-600 leading-relaxed mb-6">{featuredPost.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiUser} className="text-gray-400" />
                        <span>{featuredPost.author}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiCalendar} className="text-gray-400" />
                        <span>{featuredPost.date}</span>
                      </div>
                      <span>{featuredPost.readTime}</span>
                    </div>
                    <Link 
                      to={`/blog/${featuredPost.slug}`}
                      className="flex items-center space-x-2 text-teal-600 font-semibold hover:text-teal-700 transition-colors"
                    >
                      <span>Read More</span>
                      <SafeIcon icon={FiArrowRight} />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Category Filter and Search */}
      <motion.section 
        className="pb-12 px-4"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          {/* Search Bar */}
          <motion.div 
            className="max-w-2xl mx-auto mb-8"
            variants={fadeInUp}
          >
            <div className="bg-white rounded-full shadow-lg p-2 flex items-center">
              <SafeIcon icon={FiSearch} className="text-gray-400 ml-4 mr-2" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 outline-none text-gray-900"
              />
              {(searchTerm || selectedCategory !== 'All') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('All');
                  }}
                  className="text-gray-400 hover:text-gray-600 mr-4 text-sm"
                >
                  Clear
                </button>
              )}
            </div>
          </motion.div>

          {/* Category Filter */}
          <motion.div 
            className="flex flex-wrap justify-center gap-4"
            variants={fadeInUp}
          >
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                  selectedCategory === category 
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg' 
                    : 'bg-white text-gray-600 hover:text-gray-900 hover:shadow-md'
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>

          {/* Results Count */}
          {(searchTerm !== '' || selectedCategory !== 'All') && (
            <motion.p 
              className="text-center text-gray-600 mt-6"
              variants={fadeInUp}
            >
              Found {regularPosts.length} article{regularPosts.length !== 1 ? 's' : ''} 
              {searchTerm && ` for "${searchTerm}"`}
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
            </motion.p>
          )}
        </div>
      </motion.section>

      {/* Blog Posts Grid */}
      <motion.section 
        className="pb-20 px-4"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          {regularPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regularPosts.map((post, index) => (
                <motion.article
                  key={index}
                  className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group"
                  variants={fadeInUp}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="relative overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-gray-700 px-3 py-1 rounded-full text-xs font-semibold">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4 text-sm">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiUser} className="text-gray-400" />
                            <span>{post.author}</span>
                          </div>
                          <span>{post.readTime}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiCalendar} className="text-gray-400" />
                          <span>{post.date}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            <motion.div 
              className="text-center py-20"
              variants={fadeInUp}
            >
              <div className="text-gray-400 text-6xl mb-4">
                <SafeIcon icon={FiSearch} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your search or filter criteria
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                }}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all duration-200"
              >
                Clear Filters
              </button>
            </motion.div>
          )}
        </div>
      </motion.section>

      {/* Newsletter Section */}
      <motion.section 
        className="py-20 px-4 bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-600"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.div 
            className="mb-8"
            variants={fadeInUp}
          >
            <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiBookOpen} className="text-3xl text-white" />
            </div>
          </motion.div>
          <motion.h2 
            className="text-4xl md:text-5xl font-bold text-white mb-6"
            variants={fadeInUp}
          >
            Stay in the Loop
          </motion.h2>
          <motion.p 
            className="text-xl text-white/90 mb-8"
            variants={fadeInUp}
          >
            Get the latest insights, tips, and stories delivered to your inbox
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
            variants={fadeInUp}
          >
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-4 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
            <button className="bg-white text-gray-900 px-8 py-4 rounded-full font-semibold hover:shadow-lg transition-all duration-200 whitespace-nowrap">
              Subscribe
            </button>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}

export default Blog;